import type { KeyboardEvent } from 'react';

/**
 * The one interaction style every clickable thing in the console shares (#145).
 *
 * Before this each screen wrote its own: some had a focus ring and some did not, hover
 * differed per screen, and a keyboard user tabbing through the console saw focus vanish
 * on half of the controls. `Button`, the filter chips, the sidebar links, the pagination,
 * the dropdown triggers, the clickable rows and the clickable cards all compose this
 * string instead, so a change to how focus looks is made here once.
 *
 * - The ring is a `box-shadow` (Tailwind's `ring`), not an outline width or a border, so
 *   showing it moves nothing — no layout shift on focus, in either direction.
 * - `active:` darkens with a filter, which is also layout-neutral.
 * - The pointer cursor for native controls comes from the base layer in `styles.css`;
 *   it is repeated here for the elements the base layer cannot see (a clickable `<tr>`
 *   or card), and `not-allowed` wins over it when the control is disabled.
 */
export const interactive =
  'cursor-pointer outline-none transition focus-visible:ring-2 focus-visible:ring-pitch/40 focus-visible:ring-offset-1 active:brightness-95 disabled:cursor-not-allowed disabled:active:brightness-100 aria-disabled:cursor-not-allowed';

/**
 * Keyboard activation for an element that is clickable but is not a `<button>` — a
 * table row, a card. Enter and Space open it, the same two keys a button answers to;
 * Space is prevented so it does not also scroll the page.
 *
 * Only when the element itself has focus: a key pressed inside one of its own links or
 * buttons belongs to that control, exactly as a click on one does.
 */
export function activateOnKey(activate: () => void) {
  return (event: KeyboardEvent<HTMLElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      activate();
    }
  };
}
